import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { api, formatError, BACKEND_URL } from "../lib/api";
import { Copy as CopyIcon, Loader2, Trash2, UserPlus, Users, X } from "lucide-react";

export default function TeamPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/business/team");
      setData(data);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createInvite = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/business/team/invites", { role: "member" });
      const fullUrl = `${window.location.origin}${data.invite_url}`;
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Invite link copied to clipboard.");
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed to create invite");
    } finally {
      setCreating(false);
    }
  };

  const copyInvite = async (token) => {
    const fullUrl = `${window.location.origin}/team/accept/${token}`;
    await navigator.clipboard.writeText(fullUrl);
    toast.success("Invite link copied.");
  };

  const cancelInvite = async (id) => {
    try {
      await api.delete(`/business/team/invites/${id}`);
      toast.success("Invite canceled.");
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed");
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this teammate? They'll lose Business access.")) return;
    try {
      await api.delete(`/business/team/members/${id}`);
      toast.success("Member removed.");
      load();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed");
    }
  };

  if (loading) {
    return <AppShell><div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="team-page">
        <header className="border-b-2 border-zinc-900 dark:border-zinc-100 pb-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2"><Users className="w-6 h-6" /><h1 className="text-2xl md:text-3xl font-black">Team</h1></div>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              Seats used: <span className="font-bold">{data?.seats_used ?? 0} / {data?.seats_total ?? 5}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={createInvite}
            disabled={creating || (data?.seats_used >= data?.seats_total)}
            data-testid="invite-create-btn"
            className="inline-flex items-center gap-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 px-4 py-2 font-bold text-sm disabled:opacity-50"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Create invite link
          </button>
        </header>

        <section>
          <h2 className="text-lg font-bold mb-3">Members ({data?.members?.length || 0})</h2>
          {data?.members?.length > 0 ? (
            <div className="border border-zinc-300 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-800">
              {data.members.map((m) => (
                <div key={m.id} className="flex items-center justify-between px-4 py-3" data-testid={`member-${m.id}`}>
                  <div>
                    <div className="font-bold text-sm">{m.name}</div>
                    <div className="text-xs text-zinc-500">{m.email} · {m.role}</div>
                  </div>
                  <button type="button" onClick={() => removeMember(m.id)} title="Remove" className="text-zinc-500 hover:text-red-600 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No teammates yet. Create an invite link above to add one.</p>
          )}
        </section>

        <section>
          <h2 className="text-lg font-bold mb-3">Pending invites ({data?.pending_invites?.length || 0})</h2>
          {data?.pending_invites?.length > 0 ? (
            <div className="border border-zinc-300 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-800">
              {data.pending_invites.map((inv) => (
                <div key={inv.id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid={`invite-${inv.id}`}>
                  <div className="text-xs">
                    <div className="font-mono text-zinc-700 dark:text-zinc-300 break-all">{`${window.location.origin}/team/accept/${inv.token}`}</div>
                    <div className="text-zinc-500 mt-1">Role: {inv.role} · Expires {new Date(inv.expires_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => copyInvite(inv.token)} title="Copy" className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800">
                      <CopyIcon className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => cancelInvite(inv.id)} title="Cancel" className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-red-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No pending invites.</p>
          )}
        </section>

        <section className="text-xs text-zinc-500 border-t border-zinc-200 dark:border-zinc-800 pt-4">
          Backend URL: {BACKEND_URL}
        </section>
        {/* Hidden ref so eslint doesn't flag unused */}
        <Link to="/business/ads" className="hidden">go to ads</Link>
      </div>
    </AppShell>
  );
}
